'use client';

import { useCallback } from 'react';
import { useOnboardingStore } from '../stores/onboarding-store';
import { isRight, isLeft } from '@shared-commons/utils';
import {
  getInstruments,
  getStudySessionsOptions,
  getSkillLevelsOptions,
  createProfile,
} from '../services/onboarding-service';
import {
  Instrument,
  ProfileSkillLevelSchema,
  SkillLevelsArraySchema,
  StudySessions,
  StudySessionsOptions,
} from '../types';
import { EventBus } from '@/shared/utils';
import { OnboardingEventKeys, OnboardingEvents } from '../events';

interface UseOnboarding {
  startOnboarding: () => void;
  instruments: Instrument[];
  instrumentsViewHandler: (args: { token: string }) => Promise<void>;
  studySessionsViewHandler: (args: { token: string }) => Promise<void>;
  endOnboarding: (args: { token: string }) => Promise<void>;
  instrumentSelectionDoHandler: (payload: { instrumentName: string }) => void;
  instrumentChildDoHandler: (payload: { childName: string }) => void;
  selectedInstrument: {
    instrumentName: string | null;
  };
  studySessionsOptions: StudySessionsOptions | null;
  profileStudySessions: StudySessions[0] | null;
  studySessionsDoHandler: (profileStudySessions: StudySessions[0] | null) => void;
  skillLevelsViewHandler: (args: { token: string }) => Promise<void>;
  skillLevelDoHandler: (payload: ProfileSkillLevelSchema | null) => void;
  skillLevelsOptions: SkillLevelsArraySchema | null;
  setProfileSkillLevel: (profileSkillLevel: ProfileSkillLevelSchema | null) => void;
  profileSkillLevel: ProfileSkillLevelSchema | null;
}

export function useOnboarding(eventBus: EventBus<OnboardingEvents>): UseOnboarding {
  const {
    setSelectedInstrument,
    setInstruments,
    instruments,
    setChildInstrument,
    selectedInstrument,
    childInstrument,
    setStudySessionsOptions,
    studySessionsOptions,
    profileStudySessions,
    setProfileStudySessions,
    setSkillLevelsOptions,
    skillLevelsOptions,
    setProfileSkillLevel,
    profileSkillLevel,
    setIsLoading,
  } = useOnboardingStore();

  const startOnboarding = useCallback(() => {
    console.log('Onboarding started');
  }, []);

  const instrumentsViewHandler = useCallback(
    async ({ token }: { token: string }): Promise<void> => {
      try {
        const result = await getInstruments(token)();
        if (isRight(result)) {
          console.log('Instruments fetched successfully:', result.value.instruments);
          setInstruments(result.value.instruments);
        } else if (isLeft(result)) {
          console.error('Error fetching instruments:', result.value.message);
        }
      } catch (error) {
        console.error('Unexpected error while fetching instruments:', error);
      }
    },
    [setInstruments],
  );

  const studySessionsViewHandler = useCallback(
    async ({ token }: { token: string }) => {
      const result = await getStudySessionsOptions(token)();
      if (isRight(result)) {
        setStudySessionsOptions(result.value);
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message);
        console.log(error, 'Error fetching study sessions options');
      }
    },
    [setStudySessionsOptions],
  );

  const skillLevelsViewHandler = useCallback(
    async ({ token }: { token: string }) => {
      const result = await getSkillLevelsOptions(token)();
      if (isRight(result)) {
        setSkillLevelsOptions(result.value);
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message);
        console.log(error, 'Error fetching skill levels options');
      }
    },
    [setSkillLevelsOptions],
  );

  /**
   * Handler per la selezione dello strumento principale
   */
  const instrumentSelectionDoHandler = useCallback(
    (payload: { instrumentName: string }) => {
      setSelectedInstrument({ instrumentName: payload.instrumentName });
      console.log('Instrument (parent) selected:', payload.instrumentName);
    },
    [setSelectedInstrument],
  );

  /**
   * Handler per la selezione dello strumento child
   */
  const instrumentChildDoHandler = useCallback(
    (payload: { childName: string }) => {
      setChildInstrument({ instrumentName: payload.childName });
      console.log('Instrument (child) selected:', payload.childName);
    },
    [setChildInstrument],
  );

  const studySessionsDoHandler = useCallback(
    (payload: StudySessions[0] | null) => {
      setProfileStudySessions(payload);
      console.log('Study session selected:', payload);
    },
    [setProfileStudySessions],
  );

  const skillLevelDoHandler = useCallback(
    (payload: ProfileSkillLevelSchema | null) => {
      setProfileSkillLevel(payload);
      console.log('Study session selected:', payload);
    },
    [setProfileSkillLevel],
  );

  const endOnboarding = useCallback(
    async ({ token }: { token: string }) => {
      const {
        profileSkillLevel,
        profileStudySessions,
        selectedInstrument,
        childInstrument,
        skillLevelsOptions,
        resetStore,
      } = useOnboardingStore.getState();

      if (
        !profileSkillLevel?.skillLevel ||
        !profileStudySessions?.description ||
        !selectedInstrument.instrumentName ||
        (!selectedInstrument.instrumentName && !childInstrument.instrumentName)
      )
        return;

      const selectedSkillLevelValue =
        skillLevelsOptions?.find(
          (option) => option.onboardingDescription === profileSkillLevel?.skillLevel,
        )?.name || skillLevelsOptions?.[0].name;

      if (!selectedSkillLevelValue) {
        return;
      }

      setIsLoading(true);
      const result = await createProfile(
        {
          skillLevel: selectedSkillLevelValue,
          instrumentName: childInstrument.instrumentName || selectedInstrument.instrumentName,
          studySession: profileStudySessions?.description,
        },
        token,
      )();
      setIsLoading(false);
      if (isRight(result)) {
        console.log(result.value);
        resetStore();
        eventBus.emit(OnboardingEventKeys.end.redirectDo, undefined);
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message);
        console.log(error, 'Error fetching skill levels options');
      }
    },
    [eventBus],
  );

  return {
    startOnboarding,
    instruments,
    instrumentsViewHandler,
    instrumentSelectionDoHandler,
    instrumentChildDoHandler,
    selectedInstrument,
    studySessionsViewHandler,
    skillLevelsViewHandler,
    skillLevelsOptions,
    studySessionsOptions,
    profileStudySessions,
    studySessionsDoHandler,
    skillLevelDoHandler,
    profileSkillLevel,
    setProfileSkillLevel,
    endOnboarding,
  };
}
